import { apiFetch } from '@/lib/api'

// ---- People ---------------------------------------------------------------

export function banUser(id: number, reason: string, durationDays?: number) {
  return apiFetch(`/api/admin/users/${id}/ban/`, {
    method: 'POST',
    body: { reason, duration_days: durationDays ?? null }
  })
}

export function unbanUser(id: number) {
  return apiFetch(`/api/admin/users/${id}/unban/`, { method: 'POST' })
}

export function forceLogoutUser(id: number) {
  return apiFetch(`/api/admin/users/${id}/force-logout/`, { method: 'POST' })
}

export function promoteUser(
  id: number,
  isStaff: boolean,
  isSuperuser: boolean
) {
  return apiFetch(`/api/admin/users/${id}/promote/`, {
    method: 'POST',
    body: { is_staff: isStaff, is_superuser: isSuperuser }
  })
}

export function resetUserPassword(id: number) {
  return apiFetch(`/api/admin/users/${id}/reset-password/`, { method: 'POST' })
}

export function verifyUser(id: number) {
  return apiFetch(`/api/admin/users/${id}/verify/`, { method: 'POST' })
}

// ---- Posts ---------------------------------------------------------------

export function hidePost(id: number, reason?: string) {
  return apiFetch(`/api/admin/posts/${id}/hide/`, {
    method: 'POST',
    body: { reason: reason ?? '' }
  })
}

export function restorePost(id: number) {
  return apiFetch(`/api/admin/posts/${id}/restore/`, { method: 'POST' })
}

export function featurePost(id: number, feature: boolean) {
  return apiFetch(`/api/admin/posts/${id}/feature/`, {
    method: 'POST',
    body: { feature }
  })
}

export function movePostToFeed(
  id: number,
  body: {
    target: 'live' | 'blocks' | 'stocks'
    community_id?: number
    stock_symbol?: string
    category_id?: number
  }
) {
  return apiFetch(`/api/admin/posts/${id}/move-feed/`, { method: 'POST', body })
}

// ---- Moderation Queue ----------------------------------------------------

export interface ModerationItem {
  kind: 'post_report' | 'greenbook_report'
  id: number
  reason: string
  details: string
  post_id: number | null
  reporter_id: number | null
  created_at: string | null
}

export interface ModerationQueueResponse {
  results: ModerationItem[]
  count: number
}

export function fetchModerationQueue(
  kind: 'all' | 'post_report' | 'greenbook_report' = 'all',
  limit = 50
) {
  const params = new URLSearchParams()
  if (kind !== 'all') params.set('kind', kind)
  params.set('limit', String(limit))
  return apiFetch<ModerationQueueResponse>(
    `/api/admin/moderation/queue/?${params.toString()}`
  )
}

export function decideModeration(
  kind: 'post_report' | 'greenbook_report',
  id: number,
  decision: 'approve' | 'reject',
  note?: string
) {
  return apiFetch('/api/admin/moderation/decide/', {
    method: 'POST',
    body: { kind, id, decision, note: note ?? '' }
  })
}

export function fetchModerationBadge(): Promise<{ pending: number }> {
  return apiFetch('/api/admin/moderation/badge/')
}

// ---- Orders --------------------------------------------------------------

export function refundOrder(id: number, reason: string, amount?: number) {
  return apiFetch(`/api/admin/orders/${id}/refund/`, {
    method: 'POST',
    body: { reason, amount: amount ?? null }
  })
}

export function cancelOrder(id: number, reason: string) {
  return apiFetch(`/api/admin/orders/${id}/cancel/`, {
    method: 'POST',
    body: { reason }
  })
}

// ---- Reservations --------------------------------------------------------

export function cancelReservation(id: number, reason: string) {
  return apiFetch(`/api/admin/reservations/${id}/cancel/`, {
    method: 'POST',
    body: { reason }
  })
}

export function verifyReservation(id: number) {
  return apiFetch(`/api/admin/reservations/${id}/verify/`, { method: 'POST' })
}

// ---- Businesses ----------------------------------------------------------

export function verifyBusiness(id: number) {
  return apiFetch(`/api/admin/businesses/${id}/verify/`, { method: 'POST' })
}
